import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, ButtonRadio, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { MemberFilter } from '@/pages/teams/components/MemberFilter'
import { cn } from '@/utils/style'

interface FilterOption {
  label: string
  value: string
  className?: string
  codes?: number[]
}

interface BulkEditAction {
  label: string
  value: string
  className?: string
  enabled?: boolean
  tooltip?: string
}

interface SnapshotFiltersProps {
  // Status filter configuration
  statusFilterType?: 'dropdown' | 'radio'
  statusOptions: FilterOption[]
  selectedStatuses: (string | number)[] | string | undefined
  onStatusChange: (value: (string | number)[] | string | undefined) => void

  // Optional member filter (admin only)
  showMemberFilter?: boolean
  selectedMembers?: string[]
  onMembersChange?: (ids: string[]) => void

  // Optional restore indicator (member only)
  showRestoreIndicator?: boolean
  remainingRestores?: number
  totalRestores?: number

  // Bulk edit actions
  selectedCount?: number
  bulkEditActions?: BulkEditAction[]
  onBulkEdit?: (action: string) => void
  bulkEditTooltip?: string

  // Optional create button
  showCreateButton?: boolean
  createButtonPath?: string
}

/**
 * Unified snapshot filters component
 * Supports both admin (with member filter) and member (with restore indicator) variants
 */
export function SnapshotFilters({
  statusFilterType = 'dropdown',
  statusOptions,
  selectedStatuses,
  onStatusChange,
  showMemberFilter = false,
  selectedMembers = [],
  onMembersChange,
  showRestoreIndicator = false,
  remainingRestores = 0,
  totalRestores = 0,
  selectedCount = 0,
  bulkEditActions = [],
  onBulkEdit,
  bulkEditTooltip = '',
  showCreateButton = false,
  createButtonPath = '/team/create',
}: SnapshotFiltersProps) {
  const { t } = useTranslation()

  // Filter enabled bulk edit actions
  const enabledBulkActions = bulkEditActions.filter((action) => action.enabled !== false)
  const hasBulkActions = enabledBulkActions.length > 0

  // Render status filter based on type
  const renderStatusFilter = () => {
    if (statusFilterType === 'radio') {
      // ButtonRadio for member view
      return (
        <ButtonRadio
          value={selectedStatuses as string}
          items={statusOptions}
          onChange={(value) => {
            const filter = statusOptions.find((f) => f.value === value)
            onStatusChange(filter?.value)
          }}
        />
      )
    }

    // Dropdown for admin/shared view
    return (
      <div className="flex flex-col gap-2">
        <label className="t-sb text-secondary">
          <Trans i18nKey="teamSnapshot.admin.statusFilter">Snapshot Status</Trans>
        </label>
        <Dropdown
          rootClassName="w-[200px]"
          options={statusOptions}
          value={selectedStatuses as (string | number)[]}
          onChange={(value) => {
            const newValue = Array.isArray(value) ? value : [value]
            onStatusChange(newValue.length === 0 ? ['all'] : newValue)
          }}
          multiple={true}
          showAllOption={true}
          placeholder={t('teamSnapshot.admin.selectStatus', 'Select status')}
        />
      </div>
    )
  }

  // Render bulk edit dropdown
  const renderBulkEdit = () => {
    if (selectedCount === 0) return null

    if (!hasBulkActions) {
      // Disabled state with tooltip
      return (
        <Tooltip title={bulkEditTooltip} placement="top">
          <div>
            <Dropdown
              rootClassName="t-sb bg-neutral-50 text-primary rounded-lg"
              className="h-11 px-4 border-none opacity-50 cursor-not-allowed"
              menuClassName="w-40"
              options={[]}
              value=""
              disabled
              placeholder={t('teamSnapshot.bulkEdit.title', 'Bulk Edit')}
            />
          </div>
        </Tooltip>
      )
    }

    // Enabled state
    const dropdown = (
      <Dropdown
        rootClassName="t-sb bg-neutral-50 text-primary rounded-lg cursor-pointer"
        className="h-11 px-4 border-none"
        menuClassName="w-40"
        options={enabledBulkActions.map((action) => ({
          label: action.label,
          value: action.value,
          className: action.className || 't-sm',
        }))}
        value=""
        onChange={(value) => onBulkEdit?.(value as string)}
        placeholder={t('teamSnapshot.bulkEdit.title', 'Bulk Edit')}
      />
    )

    return bulkEditTooltip ? (
      <Tooltip title={bulkEditTooltip} placement="top">
        <div>{dropdown}</div>
      </Tooltip>
    ) : (
      dropdown
    )
  }

  // Render restore indicator
  const renderRestoreIndicator = () => {
    if (!showRestoreIndicator) return null

    return (
      <div className="px-4 py-2 rounded-full bg-neutral-50 t-x-sb flex items-center gap-x-2">
        <div
          className={cn({
            'text-error': remainingRestores === 0,
            'text-warning': remainingRestores >= 1 && remainingRestores <= 3,
            'text-tertiary': remainingRestores > 3,
          })}
        >
          <Trans i18nKey="snapshot.restoresLeft">Snapshot restores left</Trans>
        </div>
        <div
          className={cn({
            'text-error': remainingRestores === 0,
            'text-warning': remainingRestores >= 1 && remainingRestores <= 3,
            'text-primary': remainingRestores > 3,
          })}
        >
          {remainingRestores} / {totalRestores}
        </div>
      </div>
    )
  }

  // Layout: member view uses space-between, admin view uses flex-start
  const isSpaceBetween = statusFilterType === 'radio'

  return (
    <div
      className={cn('flex items-end', {
        'justify-between': isSpaceBetween,
        'gap-3': !isSpaceBetween,
      })}
    >
      {/* Left side: Filters */}
      <div className="flex gap-3 items-end">
        {/* Member Filter (admin only) */}
        {showMemberFilter && onMembersChange && (
          <MemberFilter value={selectedMembers} onChange={onMembersChange} />
        )}

        {/* Status Filter */}
        {renderStatusFilter()}
      </div>

      {/* Right side: Actions */}
      <div className={cn('flex items-center gap-3', { 'ml-auto': !isSpaceBetween })}>
        {/* Restore Indicator (member only) */}
        {renderRestoreIndicator()}

        {/* Bulk Edit */}
        {renderBulkEdit()}

        {/* Create Button */}
        {showCreateButton && (
          <Link to={createButtonPath}>
            <Button type="secondary">
              <Trans i18nKey="snapshot.createBtn">New Instance</Trans>
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}
